/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



import { Modal } from "components/generals/Modal";
import Box from "components/generals/Box";
import Button from "components/generals/Button";
import ButtonDialog from "components/generals/ButtonDialog";
import DefaultCell from "components/generals/DefaultCell";
import PermissionWrapper from "components/generals/PermissionWrapper";
import DataTable, { Props } from "dashboard-template/examples/Tables/DataTable";
import { HeaderEnum, PersonInterface, UserInterface } from "@malmur-technologies/medicalsystem-shared";
import { FC, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { EntityPermissions, FormActionType, PermissionsUser } from "types";
import useHttp from "hooks/http.hook";
import { CoreAPI_URLs } from "constants/http.const";
import UserFullForm from "components/forms/User/UserFullForm";
import { TableProps } from "interfaces";

/**
 * User table component for displaying and managing system user accounts.
 * 
 * Features:
 * - Displays user account information in sortable data table
 * - Columns: username, email, roles (comma-separated), action buttons
 * - View/edit user functionality via modal with UserFullForm
 * - Delete user with confirmation dialog and list update
 * - Permission-based access control for all operations
 * - Role display with array-to-string conversion
 * 
 * Security:
 * - Uses HeaderEnum.RESOURCE_ID for secure user identification
 * - Permission-based rendering via PermissionWrapper
 * - Validates entity permissions before delete operations
 * 
 * Data Structure:
 * - Receives PersonInterface[] where each person has a nested user object
 * - Accessors use "user." prefix to access user properties
 * 
 * @param {TableProps<PersonInterface>} props - Table properties
 * @param {PersonInterface[]} props.modelList - Array of person records with user data
 * @param {Function} props.setModelList - Callback to update list after changes
 * 
 * @returns {JSX.Element} User data table with view, edit, and delete actions
 */
const UserTable: FC<TableProps<PersonInterface>> = (props) => {
  const { t } = useTranslation();
  const [activeEditUserModal, setActiveEditUserModal] = useState<boolean>(false);
  const [selectedPerson, setSelectedPerson] = useState<PersonInterface>();

  const [_isLoading, _error, sendRequest] = useHttp();

  const deleteHandler = (person: PersonInterface) => {
    if (!person.user?.id) return;
    sendRequest<boolean, PersonInterface>(
      CoreAPI_URLs.USERS_GENERAL_OPERATIONS,
      onUserDeleteHandler,
      "DELETE",
      null,
      [{ header: HeaderEnum.RESOURCE_ID, value: person.user.id }],
      { payload: person, successMessage: t("userTable.userDeleted") }
    );
  };
  
  const onUserDeleteHandler = (result: boolean, personDeleted: PersonInterface) => {
    const updatedList = props.modelList.filter((p) => p.id !== personDeleted.id);
    props.setModelList(updatedList);
  };

  const selectPerson = (person: PersonInterface) => {
    setSelectedPerson(person);
    setActiveEditUserModal(true);
  };

  const getUITable = (list: PersonInterface[]) => {
    return {
      columns: [
        {
          Header: t("userTable.userName"),
          accessor: "user.username",
          sortIndicatorType: "az",
          Cell: ({ value }: any) => <DefaultCell value={value} />,
        },
        {
          Header: t("userTable.name"),
          accessor: "name",
          sortIndicatorType: "az",
          Cell: ({ row }: any) => {
            const r = row?.original || {};
            const fullName = [r.name, r.lastname].filter(Boolean).join(" ");
            return <DefaultCell value={fullName} />;
          },
        },
        {
          Header: t("userTable.email"),
          accessor: "user.email",
          sortIndicatorType: "az",
          Cell: ({ value }: any) => <DefaultCell value={value} />,
        },
        {
          Header: t("userTable.roles"),
          accessor: "user.roles",
          sortIndicatorType: "az",
          Cell: ({ value }: any) => (
            <DefaultCell
              value={
                Array.isArray(value)
                  ? value.map((item) => t(`userTable.${item}`)).join(", ")
                  : t(`userTable.${value}`)
              }
            />
          ),
        },
        {
          Header: t("userTable.action"),
          accessor: "id",
          disableSortBy: true,
          Cell: ({ row }: any) => {
            const personRow = row?.original;
            return (
              <>
                <Button
                  name="viewUserButton"
                  variant="contained"
                  color="info"
                  sx={{ marginRight: 1 }}
                  onClick={() => {
                    if (personRow) {
                      selectPerson(personRow);
                    }
                  }}
                >
                  {t("userTable.view")}
                </Button>
                <PermissionWrapper
                  requiredPermissions={[PermissionsUser[EntityPermissions.USERS].DELETE]}
                >
                  <ButtonDialog
                    name="deleteUserButton"
                    onAcceptHandle={() => {
                      if (personRow) {
                        deleteHandler(personRow);
                      }
                    }}
                    buttonText={t("userTable.deleteAction")}
                    title={t("userTable.deleteConfirmation")}
                    description={t("userTable.deleteDescription")}
                    labelledbyID={"UserDelete"}
                    describedbyID={"UserDescriptionId"}
                  />
                </PermissionWrapper>
              </>
            );
          },
        },
      ],
      rows: list,
    };
  };

  const dataTableData = useMemo(() => getUITable(props.modelList), [props.modelList]);

  return (
    <>
      <DataTable {...props} table={dataTableData} canSearch isLoading={props.isLoading}
        onSortChange={props.onSortChange}
        currentSortBy={props.currentSortBy}
        currentSortOrder={props.currentSortOrder}
        />
      <Modal
        open={activeEditUserModal}
        onClose={() => {
          setActiveEditUserModal(false);
        }}
      >
        <Box>
          <UserFullForm
            isModal={true}
            model={selectedPerson?.user}
            action={FormActionType.MODIFY}
            onCloseAction={() => setActiveEditUserModal(false)}
          />
        </Box>
      </Modal>
    </>
  );
};

export default UserTable;